import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/navigation/Header';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ViolationSeverityDashboard from './components/ViolationSeverityDashboard';
import RealtimeViolationFeed from './components/RealtimeViolationFeed';
import AutomatedDetectionEngine from './components/AutomatedDetectionEngine';
import violationDetectionService from '../../services/violationDetectionService';
import realtimeViolationService from '../../services/realtimeViolationService';


const RealTimeEmploymentActViolationDetectionCenter = () => {
  const [violations, setViolations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [complianceScoreChannel, setComplianceScoreChannel] = useState(null);
  const [detectionLogChannel, setDetectionLogChannel] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [latestComplianceScore, setLatestComplianceScore] = useState(null);

  useEffect(() => {
    loadInitialData();
    setupRealtimeSubscriptions();

    return () => {
      cleanupSubscriptions();
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [violationsData, statsData, complianceScore] = await Promise.all([
        realtimeViolationService?.getLatestViolations(),
        violationDetectionService?.getViolationStatistics(),
        realtimeViolationService?.getLatestComplianceScore()
      ]);
      setViolations(violationsData);
      setStatistics(statsData);
      setLatestComplianceScore(complianceScore);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to violations
    const violationChannel = realtimeViolationService?.subscribeToViolations(
      async (violation, eventType) => {
        if (eventType === 'INSERT') {
          setViolations(prev => [violation, ...prev]);
          // Refresh statistics
          const stats = await violationDetectionService?.getViolationStatistics();
          setStatistics(stats);
        } else if (eventType === 'UPDATE') {
          setViolations(prev => 
            prev?.map(v => v?.id === violation?.id ? violation : v)
          );
        } else if (eventType === 'DELETE') {
          setViolations(prev => prev?.filter(v => v?.id !== violation?.id));
        }
      }
    );
    setRealtimeChannel(violationChannel);

    // Subscribe to compliance score updates
    const scoreChannel = realtimeViolationService?.subscribeToComplianceScores(
      (report, eventType) => {
        if (eventType === 'INSERT' || eventType === 'UPDATE') {
          setLatestComplianceScore(report);
        }
      }
    );
    setComplianceScoreChannel(scoreChannel);

    // Subscribe to detection log for instant alerts
    const logChannel = realtimeViolationService?.subscribeToViolationDetectionLog(
      (detectionLog) => {
        console.log('New violation detected:', detectionLog);
        // Optionally show toast notification
      }
    );
    setDetectionLogChannel(logChannel);
  };

  const cleanupSubscriptions = () => {
    if (realtimeChannel) {
      realtimeViolationService?.unsubscribe(realtimeChannel);
    }
    if (complianceScoreChannel) {
      realtimeViolationService?.unsubscribe(complianceScoreChannel);
    }
    if (detectionLogChannel) {
      realtimeViolationService?.unsubscribe(detectionLogChannel);
    }
  };

  const handleRefresh = async () => {
    await loadInitialData();
  };

  const filteredViolations = violations?.filter(v => {
    if (filterSeverity !== 'all' && v?.severity !== filterSeverity) return false;
    if (filterStatus !== 'all' && v?.violationStatus !== filterStatus) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="AlertTriangle" size={32} color="var(--color-error)" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                Real-Time Employment Act Violation Detection Center
              </h1>
            </div>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Comprehensive automated monitoring with instant violation alerts and one-click remediation workflows
            </p>
          </div>

          {/* Main Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Violation Severity Dashboard - 4 cols */}
            <div className="lg:col-span-4">
              <ViolationSeverityDashboard 
                statistics={statistics}
                loading={loading}
                onRefresh={handleRefresh}
              />
            </div>

            {/* Main Monitoring Panel - 8 cols */}
            <div className="lg:col-span-8">
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Filters */}
                <div className="p-4 border-b border-border bg-muted/30">
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      value={filterSeverity}
                      onChange={(e) => setFilterSeverity(e?.target?.value)}
                      className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground"
                    >
                      <option value="all">All Severities</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e?.target?.value)}
                      className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground"
                    >
                      <option value="all">All Statuses</option>
                      <option value="identified">Active</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Resolved</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Filter"
                      onClick={() => {
                        setFilterSeverity('all');
                        setFilterStatus('all');
                      }}
                    >
                      Clear
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                      <span className="text-xs text-muted-foreground">Live Monitoring Active</span>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-border">
                  <div className="flex items-center gap-1 p-2">
                    <button className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground">
                      <Icon name="Activity" size={16} className="inline mr-2" />
                      Real-Time Feed
                    </button>
                    <button className="px-4 py-2 text-sm font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50">
                      <Icon name="Settings" size={16} className="inline mr-2" />
                      Detection Engine
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <RealtimeViolationFeed 
                    violations={filteredViolations}
                    loading={loading}
                    onRefresh={handleRefresh}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Automated Detection Engine */}
          <div className="mt-6">
            <AutomatedDetectionEngine />
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default RealTimeEmploymentActViolationDetectionCenter;